'use client';

import { Box, Button, Drawer } from 'src/components';
import SearchPreview from '../EncountersDrawer/components/SearchPreview';
import {
  BillingCodes,
  SaveVolumeType,
  VolumeTypeSelection,
} from './components';
import { useEncounters } from 'src/context/providers/EncountersProvider';
import { useNotification } from 'src/context/notification';
import { VolumeType } from 'src/context/reducers/encountersReducer';
import { createProviderCohort } from 'src/api/providerCohorts/providerCohortsApi';
import { useCreateFilterSetMutation } from 'src/api/redux/filterSets';
import { useState, useCallback } from 'react';
import { CreateProviderCohortData } from 'src/api/redux/providerCohorts';
import { LOCATION } from 'src/hooks/filters/constants';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import { useSavedVolume } from 'src/hooks/savedVolume/useSavedVolume';
import { IconButton } from 'src/components/PulseUI/IconButton';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import {
  buildCohortLanguage,
  filtersFromCohortLanguage,
  mapContextToCodeType,
} from 'src/components/Encounters/utils';

interface CreateVolumeTypeProps {
  hideEncounterType?: boolean;
  onSuccess?: (filterSet: any) => void;
}

const CreateVolumeType = ({ hideEncounterType = false, onSuccess }: CreateVolumeTypeProps) => {
  const { state } = useEncounters();
  const { setNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [createFilterSet] = useCreateFilterSetMutation();

  const { closeDrawer } = useDrawer();
  const { setSavedVolume } = useSavedVolume(LOCATION.DISCOVERY);
  const isCreateDisabled =
    !state.newCohortName.trim() || // Check if volume name is empty
    state.billingCodes.groups.length === 0 || // Check if there are no groups
    state.billingCodes.groups.some((group) => group.codes.length === 0) || // Check if any group is empty
    isLoading; // Disable button while loading

  const volumeTypeSelection = state.volumeType as VolumeType;

  const buildFilterSetRequestBody = useCallback(() => {
    const { newCohortName, selectedType, billingCodes } = state;

    const codesByType: Record<string, string[]> = {};

    billingCodes.groups.forEach((group) => {
      group.codes.forEach((code) => {
        const normalizedType = mapContextToCodeType(code.context_type);

        if (!codesByType[normalizedType]) {
          codesByType[normalizedType] = [];
        }

        codesByType[normalizedType].push(code.id);
      });
    });

    const filters = Object.entries(codesByType).map(([type, values]) => ({
      type,
      is_exclude: false,
      is_all: false,
      values,
    }));

    return {
      label: newCohortName,
      type: 'CODE' as const,
      scope: selectedType,
      filters,
    };
  }, [state]);

  const handleCreateClick = async () => {
    setIsLoading(true);

    try {
      if (volumeTypeSelection === VolumeType.ENCOUNTERS) {
        const { newCohortName, selectedType, billingCodes } = state;
        const cohort_language = buildCohortLanguage(billingCodes);

        const reqBody: CreateProviderCohortData = {
          label: newCohortName,
          scope: selectedType,
          cohort_language,
        };

        const response = await createProviderCohort(reqBody);
        const filters = filtersFromCohortLanguage(reqBody.cohort_language);

        setSavedVolume({
          ...response,
          cohort_language: reqBody.cohort_language,
          filters,
          volumeType: 'cohort' as const,
        });

        setNotification({
          title: 'Success',
          message: `Encounter Cohort "${response?.label}" created successfully`,
          type: 'success',
        });
      } else if (volumeTypeSelection === VolumeType.TOTAL_CLAIMS) {
        const requestBody = buildFilterSetRequestBody();

        // Use RTK Query mutation - cache invalidation happens automatically via invalidatesTags
        const response = await createFilterSet({ data: requestBody }).unwrap();

        if (onSuccess) {
          onSuccess(response);
        } else {
          setSavedVolume({
            ...response,
            volumeType: 'filterSet' as const,
            filters: requestBody.filters,
          } as any);
        }

        setNotification({
          title: 'Success',
          message: `Code Set "${
            response?.label || state.newCohortName
          }" created successfully`,
          type: 'success',
        });
      }
    } catch (error) {
      console.error('Error creating volume:', error);

      // Show error notification
      setNotification({
        title: 'Error',
        message:
          error?.error_message || 'Failed to create volume. Please try again.',
        type: 'error',
      });
    } finally {
      setIsLoading(false);
      closeDrawer('encountersDrawer');
    }
  };

  const handleCancel = () => {
    closeDrawer('encountersDrawer');
  };

  return (
    <Box className="flex flex-col h-full relative">
      <Drawer.Header className="bg-sky-100 text-primary border-b-0">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-2">
            <p
              className="font-bold text-lg"
              data-testid="create-volume-type-drawer-title"
            >
              New Code Set
            </p>
          </div>
          <div className="flex items-center gap-2">
            <IconButton
              variant="plain"
              color="neutral"
              size="sm"
              onClick={handleCancel}
              icon={faXmark}
              className="text-primary hover:bg-sky-200"
              data-testid="create-volume-type-drawer-close-button"
            />
          </div>
        </div>
      </Drawer.Header>

      <Drawer.Content>
        <VolumeTypeSelection hideEncounterType={hideEncounterType} />
        <BillingCodes />
        <SearchPreview />
        <SaveVolumeType showVolumeTypes />
      </Drawer.Content>
      <Drawer.Footer>
        <Box className="flex w-full">
          <Button
            variant="outlined"
            className="w-full !rounded-full mr-2"
            onClick={() => closeDrawer('encountersDrawer')}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="solid"
            className="w-full !rounded-full bg-sky-600 hover:bg-sky-800"
            disabled={isCreateDisabled}
            onClick={handleCreateClick}
            loading={isLoading}
          >
            Create
          </Button>
        </Box>
      </Drawer.Footer>
    </Box>
  );
};

export default CreateVolumeType;
